/** Pullback entry controls for positional theta
 * (docs/positional_theta_pullback.md).
 *
 * Arming is deliberate and single-shot: while armed the cross entry stands
 * down, and the engine clears the arm the moment a basket opens. `locked`
 * mirrors the server's 409 guard — a basket is open, so the rule may not
 * change under a live position. Disabling here is cosmetic only; the
 * backend is the real gate.
 *
 * 2026-08-01 — the operator may name an **entry price** instead of a
 * trigger mechanic. With a level set, either a counter-trend candles
 * pullback or a liquidity sweep enters, provided it happened at that
 * price, so the Trigger picker has nothing left to decide and is hidden.
 * Empty price = the original rule, mode and all.
 *
 * Lives in its own file because the parent card was at the 400-line lint
 * ceiling. */

import { useState, type ReactElement } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ApiError, strategiesApi } from "@/lib/api";
import type { PullbackMode, Strategy, StrategyUpdateRequest } from "@/types/strategy";
import { STRATEGIES_KEY } from "./useStrategyActions";

const STATUS_KEY = (id: string) => ["theta-status", id] as const;

interface Props {
  strategy: Strategy;
  onSaved: (msg: string) => void;
  onError: (msg: string) => void;
}

function savedMessage(vars: StrategyUpdateRequest): string {
  return vars.pb_enabled === false ? "Pullback disarmed" : "Pullback armed";
}

/** A 409 is the server's open-basket lock — surfaced verbatim, because it
 * means the click was correctly refused. */
function usePullbackMutation(
  strategy: Strategy,
  onSaved: (msg: string) => void,
  onError: (msg: string) => void,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (next: StrategyUpdateRequest) => strategiesApi.update(strategy.id, next),
    onSuccess: (_res, vars) => {
      void qc.invalidateQueries({ queryKey: STRATEGIES_KEY });
      void qc.invalidateQueries({ queryKey: STATUS_KEY(strategy.id) });
      onSaved(savedMessage(vars));
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 409) {
        onError("Pullback is locked while a basket is open");
        return;
      }
      const reason = err instanceof ApiError ? `HTTP ${String(err.status)}` : "network";
      onError(`Pullback save failed: ${reason}`);
    },
  });
}

function PullbackBadges({
  armed,
  locked,
}: Readonly<{ armed: boolean; locked: boolean }>): ReactElement {
  return (
    <div className="mb-2 flex flex-wrap items-center gap-2">
      <span className="label mb-0">Pullback entry</span>
      {armed ? (
        <span className="pill bg-accent-bg text-accent">
          <i className="ti ti-target text-xs" />
          Armed
        </span>
      ) : (
        <span className="pill bg-surface-2 text-ink-secondary">Off</span>
      )}
      {locked && (
        <span className="pill bg-surface-2 text-ink-muted">
          <i className="ti ti-lock text-xs" />
          Locked — basket open
        </span>
      )}
    </div>
  );
}

function PriceInput({
  price,
  setPrice,
  frozen,
}: Readonly<{ price: string; setPrice: (p: string) => void; frozen: boolean }>): ReactElement {
  return (
    <label className="block">
      <span className="label">Entry price (optional)</span>
      <input
        type="number"
        className="input"
        placeholder="any"
        value={price}
        disabled={frozen}
        onChange={(e) => {
          setPrice(e.target.value);
        }}
      />
    </label>
  );
}

/** Shown only when no price is set. They disappear rather than grey out:
 * a disabled control still reads as "this applies, you just can't change
 * it", and with a level set the mode genuinely no longer applies. */
function TriggerInputs({
  mode,
  setMode,
  candles,
  setCandles,
  frozen,
}: Readonly<{
  mode: PullbackMode;
  setMode: (m: PullbackMode) => void;
  candles: string;
  setCandles: (c: string) => void;
  frozen: boolean;
}>): ReactElement {
  return (
    <>
      <label className="block">
        <span className="label">Trigger</span>
        <select
          className="input"
          value={mode}
          disabled={frozen}
          onChange={(e) => {
            setMode(e.target.value as PullbackMode);
          }}
        >
          <option value="CANDLES">Counter-trend candles</option>
          <option value="LIQUIDITY">Liquidity taken</option>
        </select>
      </label>
      {mode === "CANDLES" && (
        <label className="block">
          <span className="label">Candles</span>
          <input
            type="number"
            className="input"
            value={candles}
            disabled={frozen}
            onChange={(e) => {
              setCandles(e.target.value);
            }}
          />
        </label>
      )}
    </>
  );
}

const TRAIL_NOTE =
  "While armed the normal cross entry stands down. One arming takes at most one basket — it disarms itself on entry.";

function explain(level: number | null): string {
  if (level === null) {
    return `Enters on a 5-min retracement while the hourly trend holds beyond both the SuperTrend and the 20 EMA. ${TRAIL_NOTE}`;
  }
  return `Long: waits for price to come DOWN to ${String(level)}, then enters when a 5-min candle either pulls back at it or takes it and closes back above. Short is the mirror. The hourly trend still picks the side. Leave the price empty to go back to picking a trigger. ${TRAIL_NOTE}`;
}

/** Null when the box is empty (no level), NaN when it holds junk — which
 * `armState` rejects rather than arming on. */
function parseLevel(price: string): number | null {
  return price.trim() === "" ? null : Number(price);
}

interface ArmState {
  /** The operator's level: null = none set, so the mode rule applies. */
  level: number | null;
  count: number;
  valid: boolean;
}

function armState(price: string, candles: string): ArmState {
  const count = Number(candles);
  const level = parseLevel(price);
  const levelOk = level === null || (Number.isFinite(level) && level > 0);
  const countOk = Number.isInteger(count) && count >= 1 && count <= 10;
  return { level: levelOk ? level : null, count, valid: countOk && levelOk };
}

export function PullbackFields({
  strategy,
  locked,
  onSaved,
  onError,
}: Readonly<Props & { locked: boolean }>): ReactElement {
  const [mode, setMode] = useState<PullbackMode>(strategy.pb_mode);
  const [candles, setCandles] = useState(String(strategy.pb_candles));
  const [price, setPrice] = useState(strategy.pb_price === null ? "" : String(strategy.pb_price));
  const armed = strategy.pb_enabled;
  const mut = usePullbackMutation(strategy, onSaved, onError);
  const { level, count, valid } = armState(price, candles);
  const frozen = locked || armed;
  return (
    <div className="mb-3 border-t border-line pt-3" data-testid="theta-pullback">
      <PullbackBadges armed={armed} locked={locked} />
      <div className="flex flex-wrap items-end gap-3">
        <PriceInput price={price} setPrice={setPrice} frozen={frozen} />
        {level === null && (
          <TriggerInputs
            mode={mode}
            setMode={setMode}
            candles={candles}
            setCandles={setCandles}
            frozen={frozen}
          />
        )}
        <button
          type="button"
          className={armed ? "btn btn-secondary" : "btn btn-primary"}
          disabled={locked || mut.isPending || (!armed && !valid)}
          onClick={() => {
            mut.mutate(
              armed
                ? { pb_enabled: false }
                : { pb_enabled: true, pb_mode: mode, pb_candles: count, pb_price: level },
            );
          }}
        >
          <i className={armed ? "ti ti-player-stop text-base" : "ti ti-target text-base"} />
          {armed ? "Disarm" : "Arm pullback"}
        </button>
      </div>
      <p className="mt-2 text-xs text-ink-muted">{explain(level)}</p>
    </div>
  );
}
